import React, { FC, useState } from "react";
import cn from "classnames";
import {
  TableRow,
  TableCell,
  notification,
  MenuButton,
  Menu,
  useCall,
  Button,
  Tooltip,
  TextEllipsis,
} from "@epcnetwork/core-ui-kit";

import { MemberModel } from "models";
import { deleteUser, reinviteUser } from "api";
import { getNameAndLastName } from "utils";

import styles from "./table-row.module.css";
import clockImg from "assets/images/clock-grey.svg";
import infoImg from "assets/images/circle-info-grey.svg";
import retryGreyImg from "assets/images/retry-circle-grey.svg";
import userPlaceholderImg from "assets/images/user.svg";
import deleteImg from "assets/images/delete-entity.svg";

type Props = {
  item: MemberModel;
  handleEdit: (id: string) => void;
};

const Row: FC<Props> = ({ item, handleEdit }: Props) => {
  const [isDeleted, setDeleted] = useState(false);
  const { submit, onCallSuccess, onCallError } = useCall(deleteUser);

  const { id, user, group, roleName } = item;

  const isPendingStatus = user?.status === "pending";

  onCallSuccess(() => {
    notification.success("User deleted", "Successfully deleted user");
  });
  onCallError(() => {
    setDeleted(false);
    notification.error("Deleting error", "Cannot delete user");
  });

  const {
    submit: submitReinvite,
    onCallSuccess: onReinviteSuccess,
    onCallError: onReinviteError,
  } = useCall(reinviteUser);

  const handleResendInvite = (id: string) => submitReinvite({ params: { userId: id } });

  onReinviteSuccess(() => {
    notification.success("Invitation resent", "Invitation resent successfully");
  });
  onReinviteError(() => {
    notification.error("Invitation resent error", "Cannot resent invitation");
  });

  const handleRemoveItem = (id: string, groupId: string) => {
    notification.confirm("Delete user ?", "You will not be able to recover it", {
      onOk: () => {
        setDeleted(true);
        submit({ params: { userId: id }, queryParams: `?groupId=${groupId}` });
      },
      okText: "Delete",
      customIcon: <img src={deleteImg} alt="" />,
    });
  };

  if (isDeleted) {
    return null;
  }

  return (
    <TableRow id={id}>
      <TableCell className={styles.cell}>
        <div className={cn(styles.user, styles.userWrapper)}>
          <img src={user?.image || userPlaceholderImg} alt="" className={styles.imageWrapper} />
          <div className={styles.userName}>
            <TextEllipsis tooltipTrigger="hover" tooltipPosition="bottom-left">
              {getNameAndLastName(user?.name, user?.lastname)}
            </TextEllipsis>
          </div>
        </div>
      </TableCell>
      <TableCell className={styles.cell}>
        <TextEllipsis tooltipTrigger="hover" tooltipPosition="bottom-left">
          {user?.email || ""}
        </TextEllipsis>
      </TableCell>
      <TableCell className={styles.cell}>{roleName || ""}</TableCell>
      <TableCell className={styles.cell}>
        {!isPendingStatus ? (
          <Tooltip
            triggerElement={
              <div>
                <img src={infoImg} alt="" />
              </div>
            }
            trigger="click"
          >
            <div className={styles.hint}>Home Groups: {group?.name}</div>
          </Tooltip>
        ) : (
          <Button appearance="text" className={styles.btn}>
            <img src={clockImg} alt="" />
            Pending
          </Button>
        )}
      </TableCell>
      <TableCell>
        <Menu>
          <MenuButton onClick={() => handleEdit(id)} icon="edit" disabled={isPendingStatus}>
            Edit
          </MenuButton>
          {isPendingStatus && (
            <MenuButton onClick={() => handleResendInvite(id)}>
              <img src={retryGreyImg} alt="" />
              Resend invite
            </MenuButton>
          )}
          <MenuButton
            appearance="delete"
            onClick={() => handleRemoveItem(user.id, group.id)}
            icon="delete"
          >
            Delete
          </MenuButton>
        </Menu>
      </TableCell>
    </TableRow>
  );
};

export default Row;
